import { Inject, Injectable } from '@angular/core';
import * as store from '@daas-consulting/core-frontend-store';
import {
  RemitosService,
  RemitosServiceId,
} from '@daas-consulting/production/remitos/frontend/data_contracts';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import {
  errorMessage,
  loader,
} from '@daas-consulting/production/shared/frontend/store';
import { Action, State, StateContext, ofActionDispatched } from '@ngxs/store';
import { EMPTY, catchError, take, tap } from 'rxjs';
import * as actions from './remitos-print.actions';
import * as consts from './remitos-print.const';
import { RemitosPrintStateModel } from './remitos-print.state-model';

export const defaults: RemitosPrintStateModel = {
  requestItem: null,
  printData: null,
  printRequest: null,
};

@State<RemitosPrintStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class RemitosPrintState {
  readonly #service: RemitosService;
  readonly #eventHelper: store.EventHelper;

  constructor(
    @Inject(RemitosServiceId) service: RemitosService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper
  ) {
    this.#service = service;
    this.#eventHelper = eventHelper;
    this._connectEvents();
  }

  @Action(actions.GetPrintData)
  getPrintData(
    ctx: StateContext<RemitosPrintStateModel>,
    { payload: remitoId }: actions.GetPrintData
  ): void {
    ctx.patchState({ requestItem: { remitoId }, printData: null });
  }

  @Action(actions.GetPrintDataRequest)
  getPrintDataRequest(ctx: StateContext<RemitosPrintStateModel>): void {
    const state = ctx.getState();
    const remitoId = state.requestItem?.remitoId as string;
    this.#service
      .getPrintData$(remitoId)
      .pipe(
        take(1),
        tap((data) =>
          ctx.dispatch(
            new actions.GetPrintDataSuccessful(
              data as models.print.PrintDataView
            )
          )
        ),
        catchError((error) => {
          ctx.dispatch(new actions.GetPrintDataFailure(error as Error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetPrintDataSuccessful)
  getPrintDataSuccessful(
    ctx: StateContext<RemitosPrintStateModel>,
    { payload }: actions.GetPrintDataSuccessful
  ): void {
    ctx.patchState({ printData: payload });
  }

  @Action(actions.GetPrintDataFailure)
  getPrintDataFailure(
    ctx: StateContext<RemitosPrintStateModel>,
    { payload }: actions.GetPrintDataFailure
  ) {
    return ctx.dispatch(new errorMessage.actions.ShowError(payload));
  }

  @Action(actions.Print)
  print(
    ctx: StateContext<RemitosPrintStateModel>,
    { payload }: actions.Print
  ): void {
    ctx.patchState({ printRequest: payload });
  }

  @Action(actions.PrintRequest)
  printRequest(ctx: StateContext<RemitosPrintStateModel>): void {
    const state = ctx.getState();
    if (!state.printRequest) {
      return;
    }
    const { remitoId } = state.printRequest;
    this.#service
      .print$(remitoId)
      .pipe(
        take(1),
        tap(() => ctx.dispatch(new actions.PrintSuccessful())),
        catchError((error) => {
          ctx.dispatch(new actions.PrintFailure(error as Error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.PrintSuccessful)
  printSuccessful(ctx: StateContext<RemitosPrintStateModel>): void {
    ctx.patchState({ printRequest: null });
  }

  @Action(actions.PrintFailure)
  printFailure(
    ctx: StateContext<RemitosPrintStateModel>,
    { payload }: actions.PrintFailure
  ) {
    return ctx.dispatch(new errorMessage.actions.ShowError(payload));
  }

  private _connectEvents(): void {
    this.#eventHelper.connectEvents(
      actions.GetPrintData,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(
      actions.GetPrintData,
      actions.GetPrintDataRequest
    );
    this.#eventHelper.connectEvents(
      actions.GetPrintDataSuccessful,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.GetPrintDataFailure,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.Print,
      loader.actions.ShowLoading,
      ofActionDispatched
    );
    this.#eventHelper.connectEvents(actions.Print, actions.PrintRequest);
    this.#eventHelper.connectEvents(
      actions.PrintSuccessful,
      loader.actions.HideLoading
    );
    this.#eventHelper.connectEvents(
      actions.PrintFailure,
      loader.actions.HideLoading
    );
  }
}
